import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Play, Pause, Save, VolumeX, Volume2, Radio } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getToken } from "@/lib/auth";
import { getPushSubscription, subscribeToPush } from "@/lib/pwa";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VOICES = [
  { id: "sage", label: "Sage — warm, measured (default)" },
  { id: "fable", label: "Fable — expressive, storyteller" },
  { id: "onyx", label: "Onyx — deep, authoritative" },
  { id: "nova", label: "Nova — energetic, upbeat" },
  { id: "shimmer", label: "Shimmer — bright, cheerful" },
  { id: "alloy", label: "Alloy — neutral, balanced" },
];

const today = () => new Date().toISOString().slice(0, 10);
const LOCAL_HEARD_KEY = "wl_voice_brief_heard";

/**
 * Fetch today's brief audio as a Blob (so playback happens from a real
 * user-gesture triggered <audio> element, which iOS requires).
 */
const fetchBriefAudioUrl = async (date, regenerate = false) => {
  const res = await fetch(`${API}/admin/daily-brief/audio?date=${date}${regenerate ? "&regenerate=1" : ""}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new Error(`Audio fetch failed (${res.status})`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
};

const fetchBriefText = async (date) => {
  const res = await axios.get(`${API}/admin/daily-brief/text`, { params: { date } });
  return res.data;
};

/* ------------------------------------------------------------------------
 * VoiceBriefCard — Settings tab
 * ---------------------------------------------------------------------- */
export const VoiceBriefCard = () => {
  const [settings, setSettings] = useState({ voice_brief_time: "07:00", voice_brief_voice: "sage", voice_brief_enabled: true });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [audioUrl, setAudioUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [transcript, setTranscript] = useState("");
  const audioRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/daily-brief/voice-settings`);
      setSettings(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/daily-brief/voice-settings`, settings);
      setSettings(data);
      // If push is already subscribed on this device, sync the new brief_time up.
      try {
        const sub = await getPushSubscription();
        if (sub) {
          await subscribeToPush({ apiBase: process.env.REACT_APP_BACKEND_URL, briefTime: data.voice_brief_time });
        }
      } catch { /* non-fatal */ }
      setDirty(false);
      toast.success("Saved.");
    } catch (e) { toast.error("Couldn't save."); }
    finally { setSaving(false); }
  };

  const playNow = async ({ regenerate = false } = {}) => {
    setBusy(true);
    try {
      const [url, text] = await Promise.all([
        fetchBriefAudioUrl(today(), regenerate),
        fetchBriefText(today()).catch(() => ({ text: "" })),
      ]);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      setAudioUrl(url);
      setTranscript(text?.text || "");
      // Wait a tick so the <audio> element picks up the new src
      requestAnimationFrame(() => {
        if (audioRef.current) {
          audioRef.current.play().catch(() => toast.error("Tap the play button to hear it."));
        }
      });
      // Mark today as heard
      try { localStorage.setItem(LOCAL_HEARD_KEY, today()); } catch { /* ignore */ }
    } catch (e) {
      toast.error("Couldn't generate the brief.");
    } finally { setBusy(false); }
  };

  const update = (patch) => { setSettings((s) => ({ ...s, ...patch })); setDirty(true); };

  return (
    <div className="bg-white border border-[#DDD1BE] rounded-2xl p-6 space-y-4" data-testid="voice-brief-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-[#0F0E0E] text-[#F5EFE6] flex items-center justify-center">
          <Radio size={16} />
        </div>
        <div>
          <div className="text-sm text-[#14100F]">Morning voice brief</div>
          <div className="text-xs text-[#6A5F58]">
            A spoken summary of today's arrivals and departures, ready to play at the time you choose.
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-[#6A5F58] flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading…</div>
      ) : (
        <>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">Ready by</Label>
              <Input
                data-testid="voice-brief-time"
                type="time"
                value={settings.voice_brief_time}
                onChange={(e) => update({ voice_brief_time: e.target.value })}
                className="mt-1 bg-[#FAF5EC] border-[#DDD1BE]"
              />
              <div className="text-xs text-[#6A5F58] mt-1">Your local time on this device.</div>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">Voice</Label>
              <select
                data-testid="voice-brief-voice"
                value={settings.voice_brief_voice}
                onChange={(e) => update({ voice_brief_voice: e.target.value })}
                className="w-full mt-1 border border-[#DDD1BE] rounded-md px-3 py-2 text-sm bg-[#FAF5EC]"
              >
                {VOICES.map((v) => (<option key={v.id} value={v.id}>{v.label}</option>))}
              </select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-[#14100F]">
            <input
              type="checkbox"
              data-testid="voice-brief-enabled"
              checked={settings.voice_brief_enabled}
              onChange={(e) => update({ voice_brief_enabled: e.target.checked })}
              className="accent-[#B76E79]"
            />
            <span>Show the play chip when I open the app at or after {settings.voice_brief_time}</span>
          </label>

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              type="button"
              onClick={() => playNow({ regenerate: false })}
              disabled={busy}
              data-testid="voice-brief-play"
              className="btn-primary font-brand text-xs tracking-[0.14em] uppercase inline-flex items-center gap-2 disabled:opacity-50"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} Play today's brief
            </button>
            <button
              type="button"
              onClick={() => playNow({ regenerate: true })}
              disabled={busy}
              className="inline-flex items-center gap-2 text-xs text-[#6A5F58] hover:text-[#B76E79] font-brand tracking-[0.14em] uppercase"
            >
              Re-record with new voice
            </button>
            {dirty && (
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="voice-brief-save"
                className="ml-auto inline-flex items-center gap-2 text-xs font-brand tracking-[0.14em] uppercase text-[#B76E79] hover:text-[#0F0E0E]"
              >
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save changes
              </button>
            )}
          </div>

          {audioUrl && (
            <div className="mt-2 space-y-2" data-testid="voice-brief-player">
              <audio
                ref={audioRef}
                src={audioUrl}
                controls
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                className="w-full"
              />
              {transcript && (
                <div className="p-3 rounded-md bg-[#FAF5EC] border border-[#EDE3D2] text-xs text-[#14100F] leading-relaxed whitespace-pre-wrap">
                  {transcript}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------------
 * MorningBriefChip — sits above the tabs and shows a play prompt when
 * the local clock has crossed the configured brief time and today's
 * brief hasn't been heard yet.
 * ---------------------------------------------------------------------- */
export const MorningBriefChip = () => {
  const [visible, setVisible] = useState(false);
  const [settings, setSettings] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const audioRef = useRef(null);

  const alreadyHeardToday = () => {
    try { return localStorage.getItem(LOCAL_HEARD_KEY) === today(); }
    catch { return false; }
  };

  const timeReached = (hhmm) => {
    if (!hhmm) return false;
    const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
    const now = new Date();
    return now.getHours() > h || (now.getHours() === h && now.getMinutes() >= m);
  };

  useEffect(() => {
    let cancelled = false;

    // If we arrived from a push tap, force show + auto-play immediately.
    const params = new URLSearchParams(window.location.search);
    const fromPush = params.get("brief") === "1";
    if (fromPush) {
      setVisible(true);
      // fire and forget — will still respect the user's tap (they just tapped the push)
      (async () => {
        try {
          const url = await fetchBriefAudioUrl(today());
          if (cancelled) return;
          setAudioUrl(url);
          requestAnimationFrame(() => {
            if (audioRef.current) audioRef.current.play().catch(() => {});
          });
          try { localStorage.setItem(LOCAL_HEARD_KEY, today()); } catch { /* ignore */ }
        } catch { /* ignore */ }
      })();
    }

    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/daily-brief/voice-settings`);
        if (cancelled) return;
        setSettings(data);
        if (!data.voice_brief_enabled) { setVisible((v) => v && fromPush); return; }
        if (alreadyHeardToday()) { setVisible((v) => v && fromPush); return; }
        setVisible(fromPush || timeReached(data.voice_brief_time));
      } catch { /* not signed in yet */ }
    };
    check();
    const t = setInterval(check, 60 * 1000); // recheck every minute
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const play = async () => {
    setBusy(true);
    try {
      const url = await fetchBriefAudioUrl(today());
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      setAudioUrl(url);
      requestAnimationFrame(() => {
        if (audioRef.current) audioRef.current.play().catch(() => {});
      });
      try { localStorage.setItem(LOCAL_HEARD_KEY, today()); } catch { /* ignore */ }
    } catch (e) {
      toast.error("Couldn't play the brief.");
    } finally { setBusy(false); }
  };

  const dismiss = () => {
    try { localStorage.setItem(LOCAL_HEARD_KEY, today()); } catch { /* ignore */ }
    setVisible(false);
    if (audioRef.current) audioRef.current.pause();
  };

  if (!visible && !audioUrl) return null;

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 bottom-4 z-40 w-[92%] max-w-md bg-white border border-[#B76E79] rounded-2xl shadow-lg px-4 py-3"
      data-testid="morning-brief-chip"
    >
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-[#B76E79] text-white flex items-center justify-center flex-shrink-0">
          <Radio size={15} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-brand tracking-[0.18em] uppercase text-[#B76E79]">Morning brief ready</div>
          <div className="text-sm text-[#14100F] truncate">
            {playing ? "Playing…" : "Tap to hear today's arrivals and departures"}
          </div>
        </div>
        {!audioUrl ? (
          <button
            type="button"
            onClick={play}
            disabled={busy}
            data-testid="morning-brief-play"
            className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition disabled:opacity-50"
            aria-label="Play morning brief"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => {
              if (!audioRef.current) return;
              if (audioRef.current.paused) audioRef.current.play(); else audioRef.current.pause();
            }}
            className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition"
            aria-label={playing ? "Pause" : "Play"}
          >
            {playing ? <Pause size={16} /> : <Play size={16} />}
          </button>
        )}
        <button
          type="button"
          onClick={dismiss}
          data-testid="morning-brief-dismiss"
          className="text-[#6A5F58] hover:text-[#0F0E0E] text-xs px-1"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => { setPlaying(false); dismiss(); }}
          className="hidden"
        />
      )}
    </div>
  );
};
